import { Fragment, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useOutboundEmailMode } from '../hooks/useOutboundEmailMode';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppContext } from '../components/Layout';
import Spinner from '../components/Spinner';
import { useAuth } from '../auth/AuthProvider';
import {
  useEventTemplates,
  useVisibleContacts,
  useAssignablePeople,
  usePersonLabels,
  useVisibleGoals,
  useVisibleInitiatives,
  useVisibleTasks,
} from '../hooks/useScopedData';
import { useVisibleInitiativeMessages } from '../hooks/useInitiativeMessages';
import {
  canEditTaskFully,
  canManageInitiative,
  canStartInitiative,
  canUpdateTaskStatus,
  initiativeCommittees,
  isAdmin,
  memberAssignedToTask,
} from '../lib/visibility';
import { taskIsOverdue } from '../components/StatusBadge';
import TaskNotes from '../components/TaskNotes';
import RefBadge from '../components/RefBadge';
import Drawer from '../components/Drawer';
import InitiativeForm, { type InitiativeDraft } from '../components/InitiativeForm';
import InitiativeSpecializedContacts from '../components/InitiativeSpecializedContacts';
import TaskForm, { type TaskDraft } from '../components/TaskForm';
import { PersonMultiPicker } from '../components/PersonPicker';
import { assigneePersonsOf, contactToPersonFrom, leadPersonsOf } from '../lib/personRefs';
import { DEFAULT_TASKS, dueDatesFromEventDate, initiativeFieldReady } from '../lib/eventPlan';
import {
  addTaskNote,
  createTask,
  deleteInitiativeCascade,
  updateDocFields,
  updateInitiativeFacts,
  updateTask,
} from '../lib/db';
import { createInitiativesFromDraft } from '../lib/initiatives';
import { EmailLink } from '../components/ContactLink';
import {
  initiativeInboundAddress,
  recName,
  statusIsComplete,
  type AppSettings,
  type ChangeEntry,
  type Contact,
  type PersonOption,
  type DefaultTaskKind,
  type Goal,
  type Initiative,
  type InitiativeMessage,
  type InitiativeStatus,
  type Task,
  type UserProfile,
} from '../types';

const OPEN_STATUSES: InitiativeStatus[] = ['planning', 'active'];

// ---- small helpers ----------------------------------------------------

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function fmtShort(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const FACT_WORD: Record<DefaultTaskKind, string> = {
  date: 'date',
  location: 'location',
  registration: 'registration link',
  eventChair: 'event chair',
};

/** Message shown (as a toast) when someone tries to complete a built-in task
 *  before its structured field on the Initiative is filled. */
function completionBlockFor(
  task: Task,
  nextStatus: string,
  init: Initiative,
  settings: AppSettings,
): string | null {
  const willComplete = statusIsComplete(settings.taskStatuses, nextStatus);
  if (task.defaultKind && willComplete && !initiativeFieldReady(task.defaultKind, init)) {
    const def = DEFAULT_TASKS.find((d) => d.kind === task.defaultKind)!;
    return `"${def.title}" can't be completed yet — set the ${FACT_WORD[task.defaultKind]} under Event details first.`;
  }
  return null;
}

interface FactView {
  kind: DefaultTaskKind;
  label: string;
  value: string | null;
  detail?: string | null;
  href?: string | null;
}

/** People are the working identity here: names resolve from the picker list or the server's label service (including
 *  people outside the viewer's own scope). Provided once by the page and read by the leaf components. */
interface PeopleCtxValue { options: PersonOption[]; labelFor: (personId: string) => string | null; toPerson: (contactId: string) => string | null }
const PeopleCtx = createContext<PeopleCtxValue>({ options: [], labelFor: () => null, toPerson: () => null });

function factViews(init: Initiative, labelFor: (personId: string) => string | null, toPerson: (contactId: string) => string | null): FactView[] {
  const chairNames = leadPersonsOf(init, toPerson)
    .map((id) => labelFor(id))
    .filter(Boolean) as string[];
  return [
    {
      kind: 'date',
      label: 'Date',
      value: init.eventDate
        ? fmtDate(init.eventDate) + (init.eventEndDate ? ` – ${fmtDate(init.eventEndDate)}` : '')
        : null,
      detail: init.eventDate
        ? [
            init.allDay
              ? 'All day'
              : init.startTime
                ? `${init.startTime}–${init.endTime ?? ''}`
                : null,
            init.backupDate ? `backup ${fmtShort(init.backupDate)}` : null,
          ]
            .filter(Boolean)
            .join(' · ') || null
        : 'Agree the date, then the schedule fills in',
    },
    {
      kind: 'location',
      label: 'Location',
      value: init.locationName || null,
      detail: init.locationName
        ? init.locationAddress ?? null
        : 'Venue name and address, once confirmed',
    },
    {
      kind: 'registration',
      label: 'Registration',
      value: init.registrationUrl ? 'Page ready' : null,
      detail: init.registrationUrl ? 'Open registration page' : 'Add the registration link',
      href: init.registrationUrl ?? null,
    },
    {
      kind: 'eventChair',
      label: 'Event chair',
      value: chairNames.join(', ') || init.leadPosition || null,
      detail: chairNames.length
        ? init.leadPosition ?? 'Primary event owner'
        : init.leadPosition
          ? 'Position — assign a person when known'
          : 'Who is leading this event',
    },
  ];
}

// ---- page -----------------------------------------------------------

type Toast = { id: number; kind: 'info' | 'warn' | 'ok'; text: string };
type DrawerState =
  | { kind: DefaultTaskKind; initId: string }
  | { kind: 'taskNotes'; initId: string; taskId: string }
  | null;

export default function Initiatives() {
  const { profile, settings, districtFilter, districts } = useAppContext();
  const { firebaseUser } = useAuth();
  const email = firebaseUser?.email?.toLowerCase() ?? 'unknown';
  const admin = isAdmin(profile);
  const location = useLocation();
  const navigate = useNavigate();
  // Goals & Tasks links to a linked initiative by landing here with an id to
  // jump to, instead of making the person hunt for it in the list.
  const focusInitiativeId = (location.state as { focusInitiativeId?: string } | null)
    ?.focusInitiativeId;

  const templatesQ = useEventTemplates();
  const initiativesQ = useVisibleInitiatives(profile);
  const tasksQ = useVisibleTasks(profile);
  const contactsQ = useVisibleContacts(profile);
  const peopleQ = useAssignablePeople(profile);
  const toPerson = useMemo(() => contactToPersonFrom(contactsQ.data), [contactsQ.data]);
  const labelFor = usePersonLabels(
    [...initiativesQ.data.flatMap((i) => leadPersonsOf(i, toPerson)), ...tasksQ.data.flatMap((t) => assigneePersonsOf(t, toPerson))],
    peopleQ.byId,
  );
  const peopleCtx = useMemo<PeopleCtxValue>(
    () => ({ options: peopleQ.options, labelFor: (id) => labelFor(id), toPerson }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [peopleQ.options, peopleQ.byId, toPerson, contactsQ.data],
  );
  const goalsQ = useVisibleGoals(profile);
  const messagesQ = useVisibleInitiativeMessages(profile);

  const messagesByInitiative = useMemo(() => {
    const map = new Map<string, InitiativeMessage[]>();
    for (const m of messagesQ.data) {
      const arr = map.get(m.initiativeId) ?? [];
      arr.push(m);
      map.set(m.initiativeId, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (b.receivedAt?.toMillis?.() ?? 0) - (a.receivedAt?.toMillis?.() ?? 0));
    }
    return map;
  }, [messagesQ.data]);

  const [form, setForm] = useState<Initiative | 'new' | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  // Presentation-only filter — never touches Initiative.status or any write
  // path. Off by default: the working view is active/in-progress/planning
  // work, not a history of everything a district has ever run. Complete and
  // archived Initiatives stay reachable, they just aren't the default.
  const [showHistorical, setShowHistorical] = useState(false);
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [taskEdit, setTaskEdit] = useState<{ init: Initiative; task: Task | null } | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toggleGroup = (id: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const notify = (kind: Toast['kind'], text: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 6000);
  };

  const activeTemplates = useMemo(
    () => templatesQ.data.filter((t) => t.active),
    [templatesQ.data],
  );
  const pickableCommittees = admin
    ? settings.committees
    : initiativeCommittees(profile, settings.committees);

  const initiatives = useMemo(() => {
    const rank = (s: InitiativeStatus) => (OPEN_STATUSES.includes(s) ? 0 : 1);
    let list = [...initiativesQ.data];
    if (districtFilter !== 'All') list = list.filter((x) => x.districtId === districtFilter);
    // Canonical status drives this, not a display label — the same
    // OPEN_STATUSES set the "Complete & archived" divider below already
    // uses. District counts and grouping derive from this same filtered
    // list, so they automatically reconcile with whatever is shown.
    if (!showHistorical) list = list.filter((x) => OPEN_STATUSES.includes(x.status));
    return list.sort(
      (a, b) =>
        rank(a.status) - rank(b.status) ||
        (a.eventDate ?? '9999').localeCompare(b.eventDate ?? '9999'),
    );
  }, [initiativesQ.data, districtFilter, showHistorical]);

  // Grouped by district when viewing "All Districts" — a single district
  // selection already scopes to one, so grouping would just add a lone header.
  const initiativesByDistrict = useMemo(() => {
    const map = new Map<string, Initiative[]>();
    for (const init of initiatives) {
      const arr = map.get(init.districtId) ?? [];
      arr.push(init);
      map.set(init.districtId, arr);
    }
    return map;
  }, [initiatives]);

  const tasksByInitiative = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const t of tasksQ.data) {
      if (!t.initiativeId) continue;
      const arr = map.get(t.initiativeId) ?? [];
      arr.push(t);
      map.set(t.initiativeId, arr);
    }
    for (const arr of map.values()) {
      arr.sort(
        (a, b) =>
          (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999') ||
          (a.offsetDays ?? 0) - (b.offsetDays ?? 0),
      );
    }
    return map;
  }, [tasksQ.data]);

  useEffect(() => {
    if (!focusInitiativeId) return;
    const target = initiativesQ.data.find((x) => x.id === focusInitiativeId);
    if (!target) return; // still loading — retry once initiativesQ.data updates
    // A deep link (from Goals & Tasks) must still resolve even when it
    // points at a completed/archived Initiative the default filter hides.
    if (!OPEN_STATUSES.includes(target.status)) setShowHistorical(true);
    setExpanded((prev) => new Set(prev).add(focusInitiativeId));
    setCollapsedGroups((prev) => {
      if (!prev.has(target.districtId)) return prev;
      const next = new Set(prev);
      next.delete(target.districtId);
      return next;
    });
    navigate(location.pathname, { replace: true });
    setTimeout(() => {
      document.getElementById(`init-${focusInitiativeId}`)?.scrollIntoView({ block: 'center' });
    }, 0);
  }, [focusInitiativeId, initiativesQ.data, location.pathname, navigate]);

  // Deep link from a task-assignment notification email (?task=<id>) — open
  // it once data has loaded, then drop the param so re-navigating here
  // doesn't keep reopening it after the user closes the drawer.
  const [searchParams, setSearchParams] = useSearchParams();
  const openedTaskFromLink = useRef(false);
  useEffect(() => {
    const wantedId = searchParams.get('task');
    if (!wantedId || openedTaskFromLink.current || initiativesQ.loading || tasksQ.loading) return;
    openedTaskFromLink.current = true;
    const task = tasksQ.data.find((t) => t.id === wantedId);
    const init = task ? initiativesQ.data.find((i) => i.id === task.initiativeId) : null;
    if (task && init) setTaskEdit({ init, task });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('task');
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, initiativesQ.loading, tasksQ.loading, initiativesQ.data, tasksQ.data]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const onCreate = async (
    draft: InitiativeDraft,
    template: (typeof activeTemplates)[number] | null,
  ) => {
    const { isRollout, districtCount } = await createInitiativesFromDraft(
      draft,
      template,
      settings,
      email,
    );
    setForm(null);
    notify(
      'ok',
      isRollout ? `${draft.name} created for ${districtCount} districts.` : `${draft.name} created.`,
    );
  };

  const onEdit = async (draft: InitiativeDraft) => {
    if (form === 'new' || !form) return;
    await updateDocFields<Initiative>('initiatives', form.id, {
      name: draft.name,
      committee: draft.committee,
      strategicGoalId: draft.strategicGoalId,
      leadPosition: draft.leadPosition,
      status: draft.status,
      notes: draft.notes,
    });
    setForm(null);
  };

  // Resolved from the unfiltered query, not the historical-filtered list —
  // unchecking "Show completed & archived" while a closed Initiative's
  // drawer is open must not yank the drawer out from under the user.
  const drawerInit = drawer ? initiativesQ.data.find((x) => x.id === drawer.initId) ?? null : null;
  const drawerTask =
    drawer && drawer.kind === 'taskNotes'
      ? tasksQ.data.find((t) => t.id === drawer.taskId) ?? null
      : null;

  return (
    <PeopleCtx.Provider value={peopleCtx}>
      <section className="iv-intro">
        <div>
          <h2>Initiatives</h2>
          <p>A district running one event — its readiness, ownership, and the work to pull it off.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={showHistorical}
              onChange={(e) => setShowHistorical(e.target.checked)}
            />
            <span className="subtitle">Show completed &amp; archived</span>
          </label>
          {canStartInitiative(profile) && (
            <button className="btn primary" onClick={() => setForm('new')}>
              + New Initiative
            </button>
          )}
        </div>
      </section>

      {(initiativesQ.loading || tasksQ.loading) && <div className="empty"><Spinner label="Loading…" /></div>}
      {initiativesQ.error && <div className="card err">{initiativesQ.error.message}</div>}
      {!initiativesQ.loading && initiatives.length === 0 && (
        <div className="card empty">No initiatives yet for this view.</div>
      )}

      {(() => {
        const renderList = (items: Initiative[]) => {
          const closedCount = items.filter((x) => !OPEN_STATUSES.includes(x.status)).length;
          return items.map((init, i) => {
            const closed = !OPEN_STATUSES.includes(init.status);
            const showDivider = closed && (i === 0 || OPEN_STATUSES.includes(items[i - 1].status));
            return (
              <Fragment key={init.id}>
                {showDivider && (
                  <div className="section-divider">
                    <span>Complete &amp; archived</span>
                    <span className="section-divider-count">{closedCount}</span>
                  </div>
                )}
                <InitiativeCard
                  init={init}
                  tasks={tasksByInitiative.get(init.id) ?? []}
                  messages={messagesByInitiative.get(init.id) ?? []}
                  profile={profile}
                  settings={settings}
                  goals={goalsQ.data}
                  contacts={contactsQ.data.filter((c) => c.districtId === init.districtId)}
                  email={email}
                  expanded={expanded.has(init.id)}
                  onToggle={() => toggle(init.id)}
                  onOpenForm={() => setForm(init)}
                  onOpenDrawer={(d) => setDrawer(d)}
                  onAddTask={() => setTaskEdit({ init, task: null })}
                  onEditTask={(task) => setTaskEdit({ init, task })}
                  notify={notify}
                />
              </Fragment>
            );
          });
        };

        if (districtFilter !== 'All') return renderList(initiatives);

        return settings.districts.map((d) => {
          const items = initiativesByDistrict.get(d.id) ?? [];
          if (items.length === 0) return null;
          const isCollapsed = collapsedGroups.has(d.id);
          return (
            <div key={d.id} className="iv-district-group">
              <button
                className="iv-district-head"
                onClick={() => toggleGroup(d.id)}
                aria-expanded={!isCollapsed}
              >
                <span className={`iv-chev${isCollapsed ? '' : ' is-open'}`} aria-hidden>
                  ▸
                </span>
                {d.name}
                <span className="iv-district-count">{items.length}</span>
              </button>
              {!isCollapsed && renderList(items)}
            </div>
          );
        });
      })()}

      {form && (
        <InitiativeForm
          initial={form === 'new' ? null : form}
          templates={activeTemplates}
          goals={goalsQ.data}
          settings={settings}
          districts={admin ? settings.districts : districts}
          committees={pickableCommittees}
          onClose={() => setForm(null)}
          onSave={form === 'new' ? onCreate : (draft) => onEdit(draft)}
        />
      )}

      {drawer && drawerInit && (
        <InitiativeDrawer
          state={drawer}
          init={drawerInit}
          task={drawerTask}
          tasks={tasksByInitiative.get(drawerInit.id) ?? []}
          contacts={contactsQ.data.filter((c) => c.districtId === drawerInit.districtId)}
          settings={settings}
          profile={profile}
          email={email}
          onClose={() => setDrawer(null)}
          notify={notify}
        />
      )}

      {taskEdit && (
        <TaskForm
          context={{
            title: taskEdit.init.name,
            districtId: taskEdit.init.districtId,
            committee: taskEdit.init.committee,
          }}
          initial={taskEdit.task}
          settings={settings}
          people={peopleQ.options}
          labelFor={(id) => labelFor(id)}
          toPerson={toPerson}
          districts={settings.districts.filter((d) => d.id === taskEdit.init.districtId)}
          onClose={() => setTaskEdit(null)}
          onSave={async (draft: TaskDraft) => {
            const payload = {
              title: draft.title.trim(),
              description: draft.description.trim(),
              districtId: draft.districtId,
              committee: draft.committee,
              status: draft.status,
              dueDate: draft.dueDate,
              assignedPosition: draft.assignmentMode === 'position' ? draft.assignedPosition : null,
              assignedPersonIds: draft.assignmentMode === 'people' ? draft.assignedPersonIds : [],
            };
            if (taskEdit.task) {
              await updateTask(taskEdit.task.id, payload, email);
            } else {
              await createTask({
                goalId: null,
                initiativeId: taskEdit.init.id,
                source: 'initiative',
                offsetDays: null,
                category: null,
                defaultKind: null,
                createdBy: email,
                ...payload,
              });
            }
            setTaskEdit(null);
            notify('ok', taskEdit.task ? 'Task updated.' : 'Task added.');
          }}
        />
      )}

      <Toasts items={toasts} onDismiss={(id) => setToasts((t) => t.filter((x) => x.id !== id))} />
    </PeopleCtx.Provider>
  );
}

// ---- one initiative card ------------------------------------------------

function InitiativeCard({
  init,
  tasks,
  messages,
  profile,
  settings,
  goals,
  contacts,
  email,
  expanded,
  onToggle,
  onOpenForm,
  onOpenDrawer,
  onAddTask,
  onEditTask,
  notify,
}: {
  init: Initiative;
  tasks: Task[];
  messages: InitiativeMessage[];
  profile: UserProfile;
  settings: AppSettings;
  goals: Goal[];
  contacts: Contact[];
  email: string;
  expanded: boolean;
  onToggle: () => void;
  onOpenForm: () => void;
  onOpenDrawer: (d: DrawerState) => void;
  onAddTask: () => void;
  onEditTask: (task: Task) => void;
  notify: (kind: Toast['kind'], text: string) => void;
}) {
  const closed = !OPEN_STATUSES.includes(init.status);
  const done = tasks.filter((t) => statusIsComplete(settings.taskStatuses, t.status)).length;
  const overdue = tasks.filter((t) =>
    taskIsOverdue(t, statusIsComplete(settings.taskStatuses, t.status)),
  ).length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
  const manage = canManageInitiative(profile, init);
  const goalTitle = init.strategicGoalId
    ? goals.find((g) => g.id === init.strategicGoalId)?.title
    : null;

  const meta = [
    <strong key="d">{recName(settings.districts, init.districtId)} District</strong>,
    recName(settings.committees, init.committee),
    init.eventDate ? fmtDate(init.eventDate) : 'Date TBD',
    goalTitle ? `Goal: ${goalTitle}` : null,
  ].filter(Boolean);

  const changeStatus = async (task: Task, next: string) => {
    const block = completionBlockFor(task, next, init, settings);
    if (block) {
      notify('warn', block);
      return;
    }
    await updateTask(task.id, { status: next }, email);
  };

  return (
    <article
      id={`init-${init.id}`}
      className={`iv-card${closed ? ' is-closed' : ''}${expanded ? ' is-open' : ''}`}
    >
      <header
        className="iv-header"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="iv-headmain">
          <div className="iv-titleline">
            <span className={`iv-chev${expanded ? ' is-open' : ''}`} aria-hidden>
              ▸
            </span>
            <h2>{init.name}</h2>
            <RefBadge value={init.ref} />
            <CopyEmailButton initiativeRef={init.ref} compact />
            {init.origin === 'council' && <span className="iv-origin">Council</span>}
          </div>
          <div className="iv-meta">
            {meta.map((m, i) => (
              <Fragment key={i}>
                {i > 0 && <span className="iv-dot">·</span>}
                {m}
              </Fragment>
            ))}
          </div>
          <div className="iv-progress-row">
            <span className="iv-progress-label">
              {tasks.length ? `${done} of ${tasks.length} tasks complete` : 'No tasks yet'}
            </span>
            <span className="iv-progress" aria-hidden>
              <span style={{ width: `${pct}%` }} />
            </span>
            {overdue > 0 && <span className="iv-overdue">{overdue} overdue</span>}
          </div>
        </div>
        <div className="iv-headactions">
          <span className={`iv-status s-${init.status}`}>{init.status}</span>
          {manage && (
            <OverflowMenu
              init={init}
              taskCount={tasks.length}
              onEdit={onOpenForm}
              onArchiveToggle={() =>
                updateDocFields<Initiative>('initiatives', init.id, {
                  status: init.status === 'archived' ? 'planning' : 'archived',
                })
              }
              onDelete={async () => {
                if (
                  !confirm(
                    `Delete "${init.name}" and its ${tasks.length} task${tasks.length === 1 ? '' : 's'}? This can't be undone.`,
                  )
                )
                  return;
                await deleteInitiativeCascade(init.id);
                notify('ok', `${init.name} deleted.`);
              }}
            />
          )}
        </div>
      </header>

      {expanded && (
        <div className="iv-body">
          <EventDetails
            init={init}
            contacts={contacts}
            profile={profile}
            tasks={tasks}
            onEdit={(kind) => onOpenDrawer({ kind, initId: init.id })}
          />
          <SetupChecklist
            init={init}
            tasks={tasks}
            settings={settings}
            profile={profile}
            onEditFact={(kind) => onOpenDrawer({ kind, initId: init.id })}
            onStatus={changeStatus}
          />
          <InitiativeSpecializedContacts key={`${init.tenantId}:${init.id}`} initiative={init} canManage={manage} />
          <Schedule
            init={init}
            tasks={tasks}
            settings={settings}
            profile={profile}
            contacts={contacts}
            canManage={manage}
            onStatus={changeStatus}
            onNotes={(taskId) => onOpenDrawer({ kind: 'taskNotes', initId: init.id, taskId })}
            onAddTask={onAddTask}
            onEditTask={onEditTask}
          />
          <InitiativeEmail init={init} messages={messages} />
          <SecondaryInfo init={init} />
        </div>
      )}
    </article>
  );
}

// ---- inbound email ------------------------------------------------

/** "Copy email" button for an initiative's inbound address. Used in the card
 *  header and in the email section. stopPropagation so the header click
 *  doesn't also toggle the card. */
function CopyEmailButton({
  initiativeRef,
  compact,
}: {
  initiativeRef?: string | null;
  compact?: boolean;
}) {
  // Initiative inbound email is provisioned for live councils only. A demonstration council has no inbound mailbox, and
  // the shared inbound domain would route a matching number to another council's initiative — so no address is shown.
  const live = useOutboundEmailMode() === 'live';
  const address = live ? initiativeInboundAddress(initiativeRef) : null;
  const [copied, setCopied] = useState(false);
  if (!address) return null;
  return (
    <button
      type="button"
      className={`iv-copyemail${compact ? ' is-compact' : ''}`}
      title={`Copy ${address}`}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(address).then(
          () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          },
          () => {},
        );
      }}
    >
      {copied ? 'Copied ✓' : '✉ Copy email'}
    </button>
  );
}

function InitiativeEmail({
  init,
  messages,
}: {
  init: Initiative;
  messages: InitiativeMessage[];
}) {
  const live = useOutboundEmailMode() === 'live'; // see CopyEmailButton: no inbound address for demonstration councils
  const address = live ? initiativeInboundAddress(init.ref) : null;
  const [openId, setOpenId] = useState<string | null>(null);
  if (!address) return null;

  return (
    <section className="iv-section iv-email">
      <div className="iv-email-head">
        <div>
          <span className="iv-email-addr"><EmailLink email={address} /></span>
          <CopyEmailButton initiativeRef={init.ref} compact />
          <div className="subtitle">Forward or send email here to log it on this event.</div>
        </div>
      </div>

      {messages.length === 0 ? (
        <p className="subtitle">No email received yet.</p>
      ) : (
        <div className="iv-email-list">
          {messages.map((m) => (
            <div key={m.id} className="iv-email-msg">
              <button
                type="button"
                className="iv-email-row"
                onClick={() => setOpenId(openId === m.id ? null : m.id)}
              >
                <span className="iv-email-from">{m.fromName || m.fromEmail}</span>
                <span className="iv-email-subj">{m.subject}</span>
                <span className="subtitle">
                  {m.receivedAt
                    ? new Date(m.receivedAt.toMillis()).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })
                    : ''}
                </span>
              </button>
              {openId === m.id && (
                <div className="iv-email-body">
                  <div className="subtitle">
                    From {m.fromEmail}
                    {m.attachmentNames.length > 0 &&
                      ` · attachments: ${m.attachmentNames.join(', ')}`}
                  </div>
                  <pre>{m.text || '(no text)'}</pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---- overflow menu ---------------------------------------------------

function OverflowMenu({
  init,
  taskCount,
  onEdit,
  onArchiveToggle,
  onDelete,
}: {
  init: Initiative;
  taskCount: number;
  onEdit: () => void;
  onArchiveToggle: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const isTest = /test/i.test(init.name);

  return (
    <div className="iv-menu" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        className="btn iv-menu-btn"
        aria-label="Initiative actions"
        onClick={() => setOpen((o) => !o)}
      >
        •••
      </button>
      {open && (
        <div className="iv-menu-panel">
          <button
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
          >
            Edit initiative
          </button>
          <button
            onClick={() => {
              setOpen(false);
              onArchiveToggle();
            }}
          >
            {init.status === 'archived' ? 'Restore initiative' : 'Archive initiative'}
          </button>
          {isTest && (
            <button
              className="danger"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
            >
              Delete test initiative{taskCount ? ` (+${taskCount})` : ''}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---- event details -------------------------------------------------

function EventDetails({
  init,
  profile,
  tasks,
  onEdit,
}: {
  init: Initiative;
  contacts: Contact[];
  profile: UserProfile;
  tasks: Task[];
  onEdit: (kind: DefaultTaskKind) => void;
}) {
  const people = useContext(PeopleCtx);
  const manage = canManageInitiative(profile, init);
  const canEditFact = (k: DefaultTaskKind) => {
    if (manage) return true;
    const t = tasks.find((x) => x.defaultKind === k);
    return !!t && memberAssignedToTask(profile, t);
  };

  return (
    <section className="iv-section">
      <div className="iv-section-head">
        <h3>Event details</h3>
        <span className="iv-section-sub">The facts people need first</span>
      </div>
      <div className="iv-facts">
        {factViews(init, people.labelFor, people.toPerson).map((f) => (
          <div key={f.kind} className={`iv-fact${f.value ? '' : ' is-missing'}`}>
            {canEditFact(f.kind) && (
              <button className="iv-fact-edit" onClick={() => onEdit(f.kind)}>
                {f.value ? 'Edit' : 'Set'}
              </button>
            )}
            <div className="iv-fact-label">{f.label}</div>
            <div className="iv-fact-value">
              {f.href ? (
                <a href={f.href} target="_blank" rel="noreferrer">
                  {f.value}
                </a>
              ) : (
                (f.value ?? 'Not set')
              )}
            </div>
            {f.detail &&
              (f.href && f.value ? (
                <a className="iv-fact-detail" href={f.href} target="_blank" rel="noreferrer">
                  {f.detail} ↗
                </a>
              ) : (
                <div className="iv-fact-detail">{f.detail}</div>
              ))}
          </div>
        ))}
      </div>
    </section>
  );
}

// ---- setup checklist --------------------------------------------------

function SetupChecklist({
  init,
  tasks,
  settings,
  profile,
  onEditFact,
  onStatus,
}: {
  init: Initiative;
  tasks: Task[];
  settings: AppSettings;
  profile: UserProfile;
  onEditFact: (kind: DefaultTaskKind) => void;
  onStatus: (task: Task, next: string) => Promise<void>;
}) {
  const people = useContext(PeopleCtx);
  const items = DEFAULT_TASKS.map((def) => ({
    def,
    task: tasks.find((t) => t.defaultKind === def.kind) ?? null,
  }));
  const present = items.filter((x) => x.task);
  const doneCount = present.filter(
    (x) => x.task && statusIsComplete(settings.taskStatuses, x.task.status),
  ).length;

  const subFor = (kind: DefaultTaskKind): string => {
    if (kind === 'date') return init.eventDate ? fmtDate(init.eventDate) : 'Date needed';
    if (kind === 'location') return init.locationName ?? 'Location needed';
    if (kind === 'registration')
      return init.registrationUrl ? 'Link added' : 'Registration link needed';
    const names = leadPersonsOf(init, people.toPerson).length;
    return names ? 'Chair assigned' : (init.leadPosition ?? 'Chair needed');
  };

  return (
    <section className="iv-section">
      <div className="iv-section-head">
        <h3>Setup</h3>
        <span className="iv-section-sub">
          {doneCount} of {present.length} complete
        </span>
      </div>
      <div className="iv-setup">
        {items.map(({ def, task }) => {
          if (!task) {
            return (
              <div key={def.kind} className="iv-setup-item is-skipped">
                <span className="iv-check is-skipped">–</span>
                <div>
                  <div className="iv-setup-title">{def.title}</div>
                  <div className="iv-setup-sub">Skipped</div>
                </div>
              </div>
            );
          }
          const complete = statusIsComplete(settings.taskStatuses, task.status);
          const canEdit = canUpdateTaskStatus(profile, task);
          return (
            <div key={def.kind} className="iv-setup-item">
              <span className={`iv-check${complete ? '' : ' is-pending'}`}>
                {complete ? '✓' : '○'}
              </span>
              <div className="iv-setup-main">
                <button className="iv-setup-title as-link" onClick={() => onEditFact(def.kind)}>
                  {def.title}
                </button>
                <div className="iv-setup-sub">{subFor(def.kind)}</div>
                {canEdit && (
                  <select
                    className="iv-setup-status"
                    value={task.status}
                    onChange={(e) => void onStatus(task, e.target.value)}
                  >
                    {settings.taskStatuses.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---- schedule -------------------------------------------------------

function Schedule({
  init,
  tasks,
  settings,
  profile,
  contacts,
  canManage,
  onStatus,
  onNotes,
  onAddTask,
  onEditTask,
}: {
  init: Initiative;
  tasks: Task[];
  settings: AppSettings;
  profile: UserProfile;
  contacts: Contact[];
  canManage: boolean;
  onStatus: (task: Task, next: string) => Promise<void>;
  onNotes: (taskId: string) => void;
  onAddTask: () => void;
  onEditTask: (task: Task) => void;
}) {
  const rows = tasks.filter((t) => !t.defaultKind);
  if (rows.length === 0 && !canManage) return null;
  const done = rows.filter((t) => statusIsComplete(settings.taskStatuses, t.status)).length;

  return (
    <section className="iv-section">
      <div className="iv-section-head">
        <div>
          <h3>Schedule</h3>
          <span className="iv-section-sub">
            {rows.length ? `${done} of ${rows.length} complete` : 'No extra tasks yet'}
            {!init.eventDate && ' · dates appear once the date is set'}
          </span>
        </div>
        {canManage && (
          <button className="btn" style={{ padding: '4px 10px' }} onClick={onAddTask}>
            + Add task
          </button>
        )}
      </div>
      <div className="iv-schedule">
        {rows.map((t) => (
          <ScheduleRow
            key={t.id}
            task={t}
            settings={settings}
            profile={profile}
            contacts={contacts}
            onStatus={(next) => onStatus(t, next)}
            onNotes={() => onNotes(t.id)}
            onEdit={canEditTaskFully(profile, t) ? () => onEditTask(t) : null}
          />
        ))}
      </div>
    </section>
  );
}

function ScheduleRow({
  task,
  settings,
  profile,
  onStatus,
  onNotes,
  onEdit,
}: {
  task: Task;
  settings: AppSettings;
  profile: UserProfile;
  contacts: Contact[];
  onStatus: (next: string) => void;
  onNotes: () => void;
  onEdit: (() => void) | null;
}) {
  const people = useContext(PeopleCtx);
  const complete = statusIsComplete(settings.taskStatuses, task.status);
  const overdue = taskIsOverdue(task, complete);
  const notes = task.noteLog?.length ?? 0;

  return (
    <div className={`iv-task${complete ? ' is-done' : ''}`}>
      <div className="iv-task-main">
        <div className="iv-task-title">
          {task.title}
          <RefBadge value={task.ref} />
          {task.category && <span className="iv-cat">{task.category}</span>}
        </div>
        <button className="iv-notelink" onClick={onNotes}>
          {notes ? `${notes} note${notes === 1 ? '' : 's'}` : 'Add note'}
        </button>
        {onEdit && (
          <button className="iv-notelink" style={{ marginLeft: 12 }} onClick={onEdit}>
            Edit
          </button>
        )}
      </div>
      <div className="iv-task-owner">
        <span className="iv-cell-label">Owner</span>
        {task.assignedPosition
          ? `◉ ${task.assignedPosition}`
          : assigneePersonsOf(task, people.toPerson).length
            ? `● ${assigneePersonsOf(task, people.toPerson).map((id) => people.labelFor(id) ?? 'Unknown').join(', ')}`
            : 'Unassigned'}
      </div>
      <div className={`iv-task-due${overdue ? ' is-over' : ''}`}>
        <span className="iv-cell-label">Due</span>
        {task.dueDate ? fmtShort(task.dueDate) : '—'}
        {overdue && ' · overdue'}
      </div>
      <div className="iv-task-status">
        <span className="iv-cell-label">Status</span>
        {canUpdateTaskStatus(profile, task) ? (
          <select value={task.status} onChange={(e) => onStatus(e.target.value)}>
            {settings.taskStatuses.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        ) : (
          <span className={`iv-statuspill${complete ? ' is-done' : ''}`}>
            {recName(settings.taskStatuses, task.status)}
          </span>
        )}
      </div>
    </div>
  );
}


// ---- secondary (notes + changes) ----------------------------------

function SecondaryInfo({ init }: { init: Initiative }) {
  const changes = (init.changeLog ?? []).slice().reverse();
  return (
    <section className="iv-section iv-secondary">
      <details>
        <summary>Initiative notes</summary>
        <p>{init.notes || 'No general notes for this event.'}</p>
      </details>
      <details>
        <summary>Activity &amp; changes{changes.length ? ` (${changes.length})` : ''}</summary>
        <div className="iv-changes">
          {changes.length === 0 && <p>No changes recorded yet.</p>}
          {changes.map((c, i) => (
            <div key={i} className="iv-change">
              {new Date(c.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ·{' '}
              {c.by.split('@')[0]} · {c.action}
              {c.reason ? ` — "${c.reason}"` : ''}
            </div>
          ))}
        </div>
      </details>
    </section>
  );
}

// ---- drawer routing + forms --------------------------------------

function InitiativeDrawer({
  state,
  init,
  task,
  tasks,
  settings,
  profile,
  email,
  onClose,
  notify,
}: {
  state: NonNullable<DrawerState>;
  init: Initiative;
  task: Task | null;
  tasks: Task[];
  contacts: Contact[];
  settings: AppSettings;
  profile: UserProfile;
  email: string;
  onClose: () => void;
  notify: (kind: Toast['kind'], text: string) => void;
}) {
  const scheduleTasks = tasks.filter((t) => !t.defaultKind);

  const saveFact = async (
    patch: Partial<Initiative>,
    action: string,
    recomputeDate?: string,
  ) => {
    const change: ChangeEntry = { by: email, at: new Date().toISOString(), action };
    const dueDates = recomputeDate ? dueDatesFromEventDate(scheduleTasks, recomputeDate) : [];
    await updateInitiativeFacts(init.id, patch, change, dueDates);
    onClose();
    notify('ok', 'Saved.');
  };

  if (state.kind === 'taskNotes') {
    if (!task) {
      onClose();
      return null;
    }
    return (
      <Drawer
        title={task.title}
        subtitle={<RefBadge value={task.ref} />}
        onClose={onClose}
      >
        {task.description && <p className="drawer-desc">{task.description}</p>}
        <TaskNotes
          notes={task.noteLog}
          legacyNote={task.notes}
          canAdd={canUpdateTaskStatus(profile, task)}
          onAdd={(text) =>
            addTaskNote(task.id, { by: email, at: new Date().toISOString(), text }, email)
          }
        />
      </Drawer>
    );
  }

  const titles: Record<DefaultTaskKind, string> = {
    date: 'Event date',
    location: 'Location',
    registration: 'Registration',
    eventChair: 'Event chair',
  };

  return (
    <Drawer title={titles[state.kind]} subtitle={init.name} onClose={onClose}>
      {state.kind === 'date' && <DateForm init={init} onSave={saveFact} />}
      {state.kind === 'location' && <LocationForm init={init} onSave={saveFact} />}
      {state.kind === 'registration' && <RegistrationForm init={init} onSave={saveFact} />}
      {state.kind === 'eventChair' && (
        <ChairForm init={init} settings={settings} onSave={saveFact} />
      )}
    </Drawer>
  );
}

function useBusy() {
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };
  return [busy, run] as const;
}

function DateForm({
  init,
  onSave,
}: {
  init: Initiative;
  onSave: (p: Partial<Initiative>, action: string, recompute?: string) => Promise<void>;
}) {
  const [start, setStart] = useState(init.eventDate ?? '');
  const [multiDay, setMultiDay] = useState(!!init.eventEndDate);
  const [end, setEnd] = useState(init.eventEndDate ?? '');
  const [backup, setBackup] = useState(init.backupDate ?? '');
  const [allDay, setAllDay] = useState(init.allDay ?? true);
  const [startTime, setStartTime] = useState(init.startTime ?? '');
  const [endTime, setEndTime] = useState(init.endTime ?? '');
  const [busy, run] = useBusy();

  return (
    <>
      <label>Date</label>
      <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />

      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={multiDay}
          onChange={(e) => setMultiDay(e.target.checked)}
        />
        Multi-day event
      </label>
      {multiDay && (
        <>
          <label>End date</label>
          <input
            type="date"
            value={end}
            min={start || undefined}
            onChange={(e) => setEnd(e.target.value)}
          />
        </>
      )}

      <label>Backup date (weather)</label>
      <input type="date" value={backup} onChange={(e) => setBackup(e.target.value)} />

      <label className="checkbox-row">
        <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
        All day
      </label>
      {!allDay && (
        <div className="form-row">
          <div>
            <label>Start time</label>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </div>
          <div>
            <label>End time</label>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
        </div>
      )}
      <p className="hint">Setting the date fills in every schedule task's due date.</p>
      <div className="drawer-actions">
        <button
          className="btn primary"
          disabled={busy || !start}
          onClick={() =>
            void run(() =>
              onSave(
                {
                  eventDate: start,
                  eventEndDate: multiDay && end && end > start ? end : null,
                  backupDate: backup || null,
                  allDay,
                  startTime: allDay ? null : startTime || null,
                  endTime: allDay ? null : endTime || null,
                },
                `Set event date to ${start}`,
                start,
              ),
            )
          }
        >
          Save date
        </button>
      </div>
    </>
  );
}

function LocationForm({
  init,
  onSave,
}: {
  init: Initiative;
  onSave: (p: Partial<Initiative>, action: string) => Promise<void>;
}) {
  const [name, setName] = useState(init.locationName ?? '');
  const [addr, setAddr] = useState(init.locationAddress ?? '');
  const [busy, run] = useBusy();
  return (
    <>
      <label>Location name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Legion Park" />
      <label>Address</label>
      <input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Street, City, State" />
      <div className="drawer-actions">
        <button
          className="btn primary"
          disabled={busy || !name.trim() || !addr.trim()}
          onClick={() =>
            void run(() =>
              onSave(
                { locationName: name.trim(), locationAddress: addr.trim() },
                `Set location: ${name.trim()}`,
              ),
            )
          }
        >
          Save location
        </button>
      </div>
    </>
  );
}

function RegistrationForm({
  init,
  onSave,
}: {
  init: Initiative;
  onSave: (p: Partial<Initiative>, action: string) => Promise<void>;
}) {
  const [url, setUrl] = useState(init.registrationUrl ?? '');
  const [busy, run] = useBusy();
  return (
    <>
      <label>Registration URL</label>
      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://scoutingevent.com/…"
      />
      <p className="hint">Saving this completes the registration requirement for the initiative.</p>
      <div className="drawer-actions">
        <button
          className="btn primary"
          disabled={busy || !url.trim()}
          onClick={() =>
            void run(() => onSave({ registrationUrl: url.trim() }, 'Set registration link'))
          }
        >
          Save registration
        </button>
      </div>
    </>
  );
}

function ChairForm({
  init,
  settings,
  onSave,
}: {
  init: Initiative;
  settings: AppSettings;
  onSave: (p: Partial<Initiative>, action: string) => Promise<void>;
}) {
  const people = useContext(PeopleCtx);
  const [position, setPosition] = useState(init.leadPosition ?? '');
  const [ids, setIds] = useState<string[]>(() => leadPersonsOf(init, people.toPerson));
  const maxLeads = 5;
  const [busy, run] = useBusy();

  return (
    <>
      <label>Lead position (optional)</label>
      <select value={position} onChange={(e) => setPosition(e.target.value)}>
        <option value="">— none —</option>
        {settings.positions.map((p) => (
          <option key={p.name}>{p.name}</option>
        ))}
      </select>
      <label>Event chair — specific people</label>
      <PersonMultiPicker
        options={people.options}
        districtId={init.districtId}
        values={ids}
        max={maxLeads}
        onChange={setIds}
        labelFor={people.labelFor}
      />
      <p className="hint">Up to {maxLeads} specific people may be assigned.</p>
      <div className="drawer-actions">
        <button
          className="btn primary"
          disabled={busy || (ids.length === 0 && !position)}
          onClick={() =>
            void run(() => {
              const names = ids.map((id) => people.labelFor(id)).filter(Boolean).join(', ');
              return onSave(
                { leadPersonIds: ids, leadPosition: position || null },
                `Set event chair: ${names || position}`,
              );
            })
          }
        >
          Save event chair
        </button>
      </div>
    </>
  );
}

// ---- toasts --------------------------------------------------------

function Toasts({ items, onDismiss }: { items: Toast[]; onDismiss: (id: number) => void }) {
  if (items.length === 0) return null;
  return (
    <div className="iv-toasts">
      {items.map((t) => (
        <div key={t.id} className={`iv-toast s-${t.kind}`} onClick={() => onDismiss(t.id)}>
          {t.text}
        </div>
      ))}
    </div>
  );
}
